import { AnalyticsAttributes } from '@carvana/merch-components/contracts';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

type ContextType = {
  analyticsAttributes?: AnalyticsAttributes;
  analyticsId: string | null;
  updateAnalyticsAttributes: (key: string, value: any) => void;
};

const defaultContext: ContextType = {
  analyticsAttributes: {},
  analyticsId: null,
  updateAnalyticsAttributes() {}
};

export type OwnProps = React.PropsWithChildren<{
  analyticsAttributes?: AnalyticsAttributes;
  analyticsId: string | null;
}>;

const SectionAnalyticsContext = createContext<ContextType>(defaultContext);

export const SectionAnalyticsProvider = ({
  analyticsAttributes: analyticsAttributesProps,
  analyticsId,
  children
}: OwnProps) => {
  const [analyticsAttributes, setAnalyticsAttributes] =
    useState<AnalyticsAttributes>(analyticsAttributesProps ?? {});

  const updateAnalyticsAttributes = useCallback((key: string, value: any) => {
    setAnalyticsAttributes(prevAttributes => ({
      ...prevAttributes,
      [key]: value
    }));
  }, []);

  const value = useMemo(
    () => ({
      analyticsAttributes,
      analyticsId,
      updateAnalyticsAttributes
    }),
    [analyticsAttributes, analyticsId, updateAnalyticsAttributes]
  );

  return (
    <SectionAnalyticsContext.Provider value={value}>
      {children}
    </SectionAnalyticsContext.Provider>
  );
};

SectionAnalyticsProvider.displayName = 'SectionAnalyticsProvider';

export const useSectionAnalytics = () => {
  return useContext(SectionAnalyticsContext);
};

export const useAnalyticsId = () => {
  return useContext(SectionAnalyticsContext).analyticsId;
};

export function MockedSectionAnalyticsContextProvider({
  children,
  analyticsId,
  analyticsAttributes = {}
}: React.PropsWithChildren<{
  analyticsId: string;
  analyticsAttributes?: AnalyticsAttributes;
}>) {
  return (
    <SectionAnalyticsContext.Provider
      value={{
        analyticsId,
        analyticsAttributes,
        updateAnalyticsAttributes: () => {}
      }}
    >
      {children}
    </SectionAnalyticsContext.Provider>
  );
}

export default SectionAnalyticsContext;
